import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Check, Circle, Edit3, UserPlus, FileText, Send, Clock, Users, CheckCircle } from 'lucide-react';
import { ProcessStageDto, ProcessStageStatus } from '../../services/committeesService';

interface HorizontalTimelineProps {
  stages: ProcessStageDto[];
  onStageClick?: (stage: ProcessStageDto) => void;
  loading?: boolean;
}

const pulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(0, 115, 234, 0.4);
  }
  50% {
    box-shadow: 0 0 0 12px rgba(0, 115, 234, 0);
  }
`;

const TimelineContainer = styled.div`
  padding: 32px 24px;
  overflow-x: auto;
  direction: rtl;

  @media (max-width: 767px) {
    padding: 24px 16px;
  }
`;

const TimelineWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  min-width: fit-content;
  gap: 0;
`;

const StageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  min-width: 120px;

  @media (max-width: 767px) {
    min-width: 100px;
  }
`;

const NodeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: relative;
  height: 56px; /* Fixed height to ensure consistent alignment */
`;

const Connector = styled.div<{ $status: 'completed' | 'current' | 'pending' }>`
  flex: 1;
  height: 3px;
  background: ${props => {
    if (props.$status === 'completed') return '#16a34a';
    return 'var(--border-color)';
  }};
`;

const NodeContainer = styled.div<{ $status: ProcessStageStatus; $isClickable: boolean }>`
  position: relative;
  z-index: 1;
  cursor: ${props => props.$isClickable ? 'pointer' : 'default'};
  transition: transform 0.2s ease;
  flex-shrink: 0;

  &:hover {
    ${props => props.$isClickable && `transform: scale(1.05);`}
  }
`;

const Node = styled.div<{ $status: ProcessStageStatus }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  ${props => {
    switch (props.$status) {
      case ProcessStageStatus.Completed:
        return css`
          background: #16a34a;
          border: 3px solid #16a34a;
          color: white;
        `;
      case ProcessStageStatus.Current:
        return css`
          width: 52px;
          height: 52px;
          background: white;
          border: 3px solid var(--primary-color);
          color: var(--primary-color);
          animation: ${pulse} 2s ease-in-out infinite;
        `;
      default:
        return css`
          background: var(--background-secondary);
          border: 2px dashed var(--border-color);
          color: var(--text-tertiary);
        `;
    }
  }}
`;

const EditBadge = styled.div`
  position: absolute;
  top: -4px;
  left: -4px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--primary-color);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const StageInfo = styled.div`
  margin-top: 12px;
  text-align: center;
  max-width: 120px;
`;

const StageName = styled.div<{ $status: ProcessStageStatus }>`
  font-size: 13px;
  font-weight: 600;
  color: ${props => {
    if (props.$status === ProcessStageStatus.Completed) return '#16a34a';
    if (props.$status === ProcessStageStatus.Current) return 'var(--primary-color)';
    return 'var(--text-tertiary)';
  }};
  margin-bottom: 4px;
  line-height: 1.3;
`;

const StageStatus = styled.div<{ $status: ProcessStageStatus }>`
  font-size: 11px;
  color: ${props => {
    if (props.$status === ProcessStageStatus.Completed) return '#16a34a';
    if (props.$status === ProcessStageStatus.Current) return 'var(--primary-color)';
    return 'var(--text-tertiary)';
  }};
  font-weight: 500;
`;

const StageDate = styled.div`
  font-size: 11px;
  color: var(--text-secondary);
  margin-top: 2px;
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 40px;
  color: var(--text-secondary);
`;

const EmptyContainer = styled.div`
  text-align: center;
  padding: 40px;
  color: var(--text-secondary);
`;

const getStageIcon = (stageName: string, status: ProcessStageStatus): React.ReactNode => {
  const size = status === ProcessStageStatus.Current ? 24 : 20;
  const iconMap: { [key: string]: React.ReactNode } = {
    'לקוח חדש': <UserPlus size={size} />,
    'איסוף מסמכים': <FileText size={size} />,
    'הגשת תביעה': <Send size={size} />,
    'ממתין לוועדה': <Clock size={size} />,
    'וועדה אחרת': <Users size={size} />,
    'אושר': <CheckCircle size={size} />,
  };

  // Check for completed status first
  if (status === ProcessStageStatus.Completed) {
    return <Check size={size} strokeWidth={3} />;
  }

  // Return matching icon or default
  return iconMap[stageName] || <Circle size={size} />;
};

const getStatusLabel = (status: ProcessStageStatus): string => {
  switch (status) {
    case ProcessStageStatus.Completed:
      return 'הושלם';
    case ProcessStageStatus.Current:
      return 'שלב נוכחי';
    default:
      return 'עתידי';
  }
};

const getConnectorStatus = (
  currentStageStatus: ProcessStageStatus,
  nextStageStatus?: ProcessStageStatus
): 'completed' | 'current' | 'pending' => {
  if (currentStageStatus === ProcessStageStatus.Completed) {
    if (nextStageStatus === ProcessStageStatus.Completed) return 'completed';
    if (nextStageStatus === ProcessStageStatus.Current) return 'completed';
    return 'current';
  }
  if (currentStageStatus === ProcessStageStatus.Current) return 'pending';
  return 'pending';
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  });
};

const HorizontalTimeline: React.FC<HorizontalTimelineProps> = ({
  stages,
  onStageClick,
  loading
}) => {
  if (loading) {
    return <LoadingContainer>טוען ציר זמן...</LoadingContainer>;
  }

  if (stages.length === 0) {
    return <EmptyContainer>לא הוגדרו שלבים עבור לקוח זה</EmptyContainer>;
  }

  return (
    <TimelineContainer>
      <TimelineWrapper>
        {stages.map((stage, index) => {
          const isFirst = index === 0;
          const isLast = index === stages.length - 1;
          const nextStage = stages[index + 1];
          const prevStage = stages[index - 1];
          const isCurrent = stage.status === ProcessStageStatus.Current;
          const isClickable = !!onStageClick;

          return (
            <StageWrapper key={stage.id || index}>
              <NodeRow>
                {/* Connector before node (on right side for RTL) */}
                {!isFirst && (
                  <Connector
                    $status={getConnectorStatus(prevStage?.status, stage.status)}
                  />
                )}

                {/* Node */}
                <NodeContainer
                  $status={stage.status}
                  $isClickable={isClickable}
                  onClick={() => onStageClick?.(stage)}
                >
                  <Node $status={stage.status}>
                    {getStageIcon(stage.stageName, stage.status)}
                  </Node>
                  {isCurrent && onStageClick && (
                    <EditBadge>
                      <Edit3 size={12} />
                    </EditBadge>
                  )}
                </NodeContainer>

                {/* Connector after node (on left side for RTL) */}
                {!isLast && (
                  <Connector
                    $status={getConnectorStatus(stage.status, nextStage?.status)}
                  />
                )}
              </NodeRow>

              <StageInfo>
                <StageName $status={stage.status}>{stage.stageName}</StageName>
                <StageStatus $status={stage.status}>
                  {getStatusLabel(stage.status)}
                </StageStatus>
                {stage.stageDate && (
                  <StageDate>{formatDate(stage.stageDate)}</StageDate>
                )}
              </StageInfo>
            </StageWrapper>
          );
        })}
      </TimelineWrapper>
    </TimelineContainer>
  );
};

export default HorizontalTimeline;
